/**
 * Query key factory for neighborhood state stored in React Query cache.
 * Follows the same pattern as mapStateKeys for layout↔page state sharing.
 *
 * Commute preferences and POI preferences are also persisted to localStorage
 * so they survive page refreshes.
 */

export type CommuteDestination = {
  placeId: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
};

/** Commute travel mode, in Google Maps DirectionsService vocabulary. */
export type TravelMode = 'DRIVING' | 'TRANSIT' | 'WALKING' | 'BICYCLING';

export type CommutePreferences = {
  destination: CommuteDestination | null;
  travelMode: TravelMode;
};

export type PoiPreferences = {
  enabledCategories: Array<string>;
  hasCustomSelection: boolean;
};

export type NeighborhoodMapOverlays = {
  poiMarkers: Array<{
    lat: number;
    lng: number;
    category: string;
    name: string;
  }>;
  commuteRoute: {
    encodedPolyline: string;
    durationText: string;
    distanceText: string;
  } | null;
  destinationMarker: {
    lat: number;
    lng: number;
    label: string;
  } | null;
};

export const neighborhoodStateKeys = {
  all: ['neighborhoodState'] as const,
  commutePreferences: () => [...neighborhoodStateKeys.all, 'commutePreferences'] as const,
  poiPreferences: () => [...neighborhoodStateKeys.all, 'poiPreferences'] as const,
  mapOverlays: () => [...neighborhoodStateKeys.all, 'mapOverlays'] as const,
  route: (originKey: string, destinationKey: string) =>
    [...neighborhoodStateKeys.all, 'route', originKey, destinationKey] as const,
};
